import { ICommand } from './ICommand';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import { useDiagramStore } from '../../stores/diagramStore';
import type { TableCellKey } from '../../stores/tableCellStore';
import type { TableCellStyle } from '../../domains/diagram/models/table/tableCell.types';
import { getTableCellRectByKey } from '../../domains/diagram/shapes/table/tableDrop.utils';
import type { TableCellClipboardPayload } from '../../domains/diagram/shapes/table/tableCellClipboard';

type TableDock = { tableId: string; cellKey: `${number},${number}`; offset?: { x: number; y: number } };

type Params = {
  tableId: string;
  cellKey: TableCellKey;
  payload: TableCellClipboardPayload;
  dockMode?: 'fixed' | 'grid';
};

export class PasteTableCellContentCommand implements ICommand {
  id = `paste-table-cell-content-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  name = 'Paste Table Cell Content';
  timestamp = Date.now();

  private previousText?: string;
  private hadPrevTextKey = false;
  private previousStyle?: TableCellStyle;
  private hadPrevStyleKey = false;
  private previousByCell = new Map<TableCellKey, { hadText: boolean; text?: string; hadStyle: boolean; style?: TableCellStyle }>();
  private createdNodeIds: string[] = [];

  constructor(private params: Params) {}

  async execute(): Promise<void> {
    const mm = getNodeMutationManager();
    const store = useDiagramStore.getState();
    const table = store.nodes.find((n) => n.id === this.params.tableId) as any;
    if (!table || table.type !== 'table') return;

    const hit = getTableCellRectByKey(table, this.params.cellKey as any);
    if (!hit) return;
    const cellKey = hit.cellKey as TableCellKey;

    const attrs = (table.attrs ?? {}) as Record<string, unknown>;
    const prevTextMap = (attrs.cellText as Record<string, string> | undefined) ?? {};
    const prevStyleMap = (attrs.cellStyles as Record<string, TableCellStyle> | undefined) ?? {};
    this.previousByCell.clear();

    this.hadPrevTextKey = Object.prototype.hasOwnProperty.call(prevTextMap, cellKey);
    this.previousText = prevTextMap[cellKey];
    this.hadPrevStyleKey = Object.prototype.hasOwnProperty.call(prevStyleMap, cellKey);
    this.previousStyle = prevStyleMap[cellKey];

    const nextTextMap: Record<string, string> = { ...prevTextMap };
    const nextStyleMap: Record<string, TableCellStyle> = { ...prevStyleMap };

    const payloadCells = this.params.payload.cells?.items ?? [];
    if (payloadCells.length > 0) {
      const rowCount = typeof (attrs as any).rowCount === 'number' ? Math.max(1, Math.floor((attrs as any).rowCount)) : 3;
      const colCount = typeof (attrs as any).columnCount === 'number' ? Math.max(1, Math.floor((attrs as any).columnCount)) : 3;
      const showHeader = (attrs as any).showHeader !== false;
      const renderRowCount = showHeader ? rowCount + 1 : rowCount;
      const [baseRStr, baseCStr] = String(cellKey).split(',');
      const baseR = Number(baseRStr);
      const baseC = Number(baseCStr);
      if (!Number.isFinite(baseR) || !Number.isFinite(baseC)) return;
      payloadCells.forEach((item) => {
        const targetR = baseR + item.r;
        const targetC = baseC + item.c;
        if (targetR < 0 || targetC < 0 || targetR >= renderRowCount || targetC >= colCount) return;
        const targetKey = `${targetR},${targetC}` as TableCellKey;
        if (!this.previousByCell.has(targetKey)) {
          this.previousByCell.set(targetKey, {
            hadText: Object.prototype.hasOwnProperty.call(prevTextMap, targetKey),
            text: prevTextMap[targetKey],
            hadStyle: Object.prototype.hasOwnProperty.call(prevStyleMap, targetKey),
            style: prevStyleMap[targetKey],
          });
        }
        const nextTextRaw = typeof item.text === 'string' ? item.text : '';
        const normalized = nextTextRaw.trim();
        if (normalized.length === 0) delete nextTextMap[targetKey];
        else nextTextMap[targetKey] = nextTextRaw;

        const style = item.style;
        if (style && typeof style === 'object') nextStyleMap[targetKey] = JSON.parse(JSON.stringify(style)) as TableCellStyle;
        else delete nextStyleMap[targetKey];
      });
      mm.applyNodeChange(this.params.tableId, { attrs: { cellText: nextTextMap, cellStyles: nextStyleMap } } as any);
    } else {
      this.previousByCell.set(cellKey, {
        hadText: this.hadPrevTextKey,
        text: this.previousText,
        hadStyle: this.hadPrevStyleKey,
        style: this.previousStyle,
      });
      const nextTextRaw = typeof this.params.payload.text === 'string' ? this.params.payload.text : '';
      const normalized = nextTextRaw.trim();
      if (normalized.length === 0) delete nextTextMap[cellKey];
      else nextTextMap[cellKey] = nextTextRaw;

      const style = this.params.payload.style;
      if (style && typeof style === 'object') nextStyleMap[cellKey] = JSON.parse(JSON.stringify(style)) as TableCellStyle;
      else delete nextStyleMap[cellKey];

      mm.applyNodeChange(this.params.tableId, { attrs: { cellText: nextTextMap, cellStyles: nextStyleMap } } as any);
    }

    const now = Date.now();
    const created: any[] = [];
    const targetRect = hit.contentRect;
    const dockMode = this.params.dockMode ?? 'fixed';
    const shapes = Array.isArray(this.params.payload.dockedShapes) ? this.params.payload.dockedShapes : [];

    shapes.forEach((s, idx) => {
      const newId = `cell-paste-${now}-${idx}-${Math.random().toString(36).slice(2)}`;
      const w = (s.w ?? s.width ?? 60) as number;
      const h = (s.h ?? s.height ?? 60) as number;
      const offX = typeof s.offset?.x === 'number' && Number.isFinite(s.offset.x) ? s.offset.x : 0;
      const offY = typeof s.offset?.y === 'number' && Number.isFinite(s.offset.y) ? s.offset.y : 0;
      const x = dockMode === 'fixed' ? targetRect.x + offX : targetRect.x + targetRect.w / 2 - w / 2;
      const y = dockMode === 'fixed' ? targetRect.y + offY : targetRect.y + targetRect.h / 2 - h / 2;
      const baseAttrs = (s.attrs && typeof s.attrs === 'object') ? { ...s.attrs } : {};
      delete (baseAttrs as any).tableDock;

      const tableDock: TableDock =
        dockMode === 'fixed'
          ? { tableId: this.params.tableId, cellKey, offset: { x: offX, y: offY } }
          : { tableId: this.params.tableId, cellKey };

      created.push({
        id: newId,
        type: s.type,
        category: s.category,
        x,
        y,
        w,
        h,
        width: w,
        height: h,
        text: s.text,
        name: s.name,
        fillColor: s.fillColor,
        strokeColor: s.strokeColor,
        strokeWidth: s.strokeWidth,
        attrs: { ...baseAttrs, tableDock },
      });
      this.createdNodeIds.push(newId);
    });

    if (created.length > 0) {
      if (created.length > 1) (mm as any).batchAddNodes(created);
      else mm.addNode(created[0]);
    }

    if (payloadCells.length > 0) {
      payloadCells.forEach((item) => {
        const [baseRStr, baseCStr] = String(cellKey).split(',');
        const baseR = Number(baseRStr);
        const baseC = Number(baseCStr);
        if (!Number.isFinite(baseR) || !Number.isFinite(baseC)) return;
        const targetKey = `${baseR + item.r},${baseC + item.c}` as TableCellKey;
        const cellHit = getTableCellRectByKey(table, targetKey as any);
        if (!cellHit) return;
        const targetRect = cellHit.contentRect;
        const dockedShapes = Array.isArray(item.dockedShapes) ? item.dockedShapes : [];
        const localCreated: any[] = [];
        dockedShapes.forEach((s, idx) => {
          const newId = `cell-paste-${now}-${idx}-${Math.random().toString(36).slice(2)}`;
          const w = (s.w ?? s.width ?? 60) as number;
          const h = (s.h ?? s.height ?? 60) as number;
          const offX = typeof s.offset?.x === 'number' && Number.isFinite(s.offset.x) ? s.offset.x : 0;
          const offY = typeof s.offset?.y === 'number' && Number.isFinite(s.offset.y) ? s.offset.y : 0;
          const x = dockMode === 'fixed' ? targetRect.x + offX : targetRect.x + targetRect.w / 2 - w / 2;
          const y = dockMode === 'fixed' ? targetRect.y + offY : targetRect.y + targetRect.h / 2 - h / 2;
          const baseAttrs = (s.attrs && typeof s.attrs === 'object') ? { ...s.attrs } : {};
          delete (baseAttrs as any).tableDock;
          const tableDock: TableDock =
            dockMode === 'fixed'
              ? { tableId: this.params.tableId, cellKey: targetKey as any, offset: { x: offX, y: offY } }
              : { tableId: this.params.tableId, cellKey: targetKey as any };
          localCreated.push({
            id: newId,
            type: s.type,
            category: s.category,
            x,
            y,
            w,
            h,
            width: w,
            height: h,
            text: s.text,
            name: s.name,
            fillColor: s.fillColor,
            strokeColor: s.strokeColor,
            strokeWidth: s.strokeWidth,
            attrs: { ...baseAttrs, tableDock },
          });
          this.createdNodeIds.push(newId);
        });
        if (localCreated.length > 0) {
          if (localCreated.length > 1) (mm as any).batchAddNodes(localCreated);
          else mm.addNode(localCreated[0]);
        }
      });
    }
  }

  async undo(): Promise<void> {
    const mm = getNodeMutationManager();
    const store = useDiagramStore.getState();
    const table = store.nodes.find((n) => n.id === this.params.tableId) as any;
    if (!table || table.type !== 'table') return;

    const hit = getTableCellRectByKey(table, this.params.cellKey as any);
    if (!hit) return;
    const cellKey = hit.cellKey as TableCellKey;

    const attrs = (table.attrs ?? {}) as Record<string, unknown>;
    const prevTextMap = (attrs.cellText as Record<string, string> | undefined) ?? {};
    const prevStyleMap = (attrs.cellStyles as Record<string, TableCellStyle> | undefined) ?? {};

    const nextTextMap: Record<string, string> = { ...prevTextMap };
    const nextStyleMap: Record<string, TableCellStyle> = { ...prevStyleMap };

    if (this.previousByCell.size > 0) {
      this.previousByCell.forEach((prev, key) => {
        if (prev.hadText && typeof prev.text === 'string') nextTextMap[key] = prev.text;
        else delete nextTextMap[key];
        if (prev.hadStyle && prev.style) nextStyleMap[key] = prev.style;
        else delete nextStyleMap[key];
      });
    } else {
      if (this.hadPrevTextKey && typeof this.previousText === 'string') nextTextMap[cellKey] = this.previousText;
      else delete nextTextMap[cellKey];

      if (this.hadPrevStyleKey && this.previousStyle) nextStyleMap[cellKey] = this.previousStyle;
      else delete nextStyleMap[cellKey];
    }

    mm.applyNodeChange(this.params.tableId, { attrs: { cellText: nextTextMap, cellStyles: nextStyleMap } } as any);

    if (this.createdNodeIds.length > 0) {
      if (this.createdNodeIds.length > 1) (mm as any).batchDeleteNodes(this.createdNodeIds);
      else mm.deleteNode(this.createdNodeIds[0]);
    }
    this.createdNodeIds = [];
  }

  async redo(): Promise<void> {
    await this.execute();
  }
}
